/**
 * Living layer — shared contract.
 *
 * The ten canonical reference PNGs stay the visual source of truth. Everything
 * in `living/` is layered on top of them in the reference pixel space
 * (1672×941, classroom 1586×992) and reads/writes the same
 * `ReferenceDemoState` the raster hotspots already drive.
 *
 * Modules (each owns its own folder, all consume this file):
 *   light/   NeonLayer — the artwork's own lime signal, extracted to
 *            public/studyninja/light/<scene>-lime.png, breathing/travelling.
 *   insets/  Live HTML regions that cover the baked-in dynamic panels of a
 *            scene so state changes are visible without leaving the artwork.
 *   lens/    Persona lens (teacher / expert / parent / investor) copy + card.
 *   tour/    Autoplay tour and proactive "try this" nudges.
 *   dock/    Floating presenter dock (scenes, lens, play, ask, share, help).
 *
 * Copy rules for every string in this feature (enforced by tests):
 *   - the learners are "synthetic"; the two claim sweeps in test/helpers/
 *     honesty-scan.mjs (both exported patterns) must find nothing new here;
 *   - never invented metrics, integrations, study results or guarantees;
 *   - research prototypes (MagicBrain, UWT) described by purpose only.
 */

import type { Lang } from '@/i18n/config';
import type { ReferenceDemoPatch, ReferenceSceneId } from '../reference/types';

export type Bilingual = Record<Lang, string>;

/** Who is looking at the demo. Drives copy, nudges, suggested questions and the AI prompt. */
export type Persona = 'teacher' | 'expert' | 'parent' | 'investor';
export const PERSONAS = ['teacher', 'expert', 'parent', 'investor'] as const satisfies readonly Persona[];
export const DEFAULT_PERSONA: Persona = 'teacher';
export function isPersona(value: unknown): value is Persona {
  return typeof value === 'string' && (PERSONAS as readonly string[]).includes(value);
}

export interface PersonaMeta {
  id: Persona;
  /** Segmented-control label. */
  label: Bilingual;
  /** One line under the label: what this lens optimizes for. */
  focus: Bilingual;
}

/** Per scene × persona narrative shown in the lens card and used by the tour. */
export interface SceneLensCopy {
  /** ≤ 64 chars. What this screen means for this persona. */
  headline: Bilingual;
  /** ≤ 180 chars. One concrete insight, grounded in what is visible on the screen. */
  insight: Bilingual;
  /** Proactive nudge: a hotspot id from `hotspotsFor()` plus what pressing it shows. ≤ 64 chars. */
  tryThis: { hotspot: string; label: Bilingual };
  /** Two suggested questions for the assistant, phrased in this persona's voice. */
  questions: readonly [Bilingual, Bilingual];
}

export type LensBook = Record<ReferenceSceneId, Record<Persona, SceneLensCopy>>;

/** One moment of the autoplay tour inside a scene. */
export interface TourBeat {
  /** Offset from scene start, ms. Beats must be sorted ascending. */
  at: number;
  /** Hotspot to spotlight (pulsing ring) while this beat is active. */
  hotspot?: string;
  /** State change applied when the beat starts (visible through insets). */
  patch?: ReferenceDemoPatch;
  /** Caption shown in the tour bar; falls back to the persona insight when omitted. */
  caption?: Bilingual;
}

export interface TourScene {
  scene: ReferenceSceneId;
  /** Total dwell time, ms. Range 6000..14000. */
  duration: number;
  beats: readonly TourBeat[];
}

export type TourScript = readonly TourScene[];

export interface TourState {
  playing: boolean;
  /** 0..1 progress inside the current scene. */
  progress: number;
  /** Active beat index, -1 before the first beat. */
  beat: number;
}

/** Everything a living module may need from the shell, without prop drilling. */
export interface LivingContextValue {
  lang: Lang;
  persona: Persona;
  setPersona: (persona: Persona) => void;
  /** Hotspot id currently spotlighted (tour beat or proactive nudge), or null. */
  spotlight: string | null;
  /** Dismiss the current nudge; the tour ignores this. */
  dismissNudge: () => void;
  tour: TourState & { play: () => void; pause: () => void; toggle: () => void };
  /** Scene navigation helpers mirrored from the shell. */
  sceneIndex: number;
  navigate: (target: ReferenceSceneId | number) => void;
  openAssistant: (seed?: string) => void;
  toggleFullscreen: () => void;
  openMap: () => void;
  reset: () => void;
  /** Copy the current deep link; resolves with the copied URL or null when the clipboard is unavailable. */
  share: () => Promise<string | null>;
  /** True when the visitor prefers reduced motion; every module must respect it. */
  reducedMotion: boolean;
  /** True on coarse-pointer devices; magnetic/hover effects are disabled there. */
  touch: boolean;
}

/** Rectangle in reference pixel space: x, y, width, height. */
export type Rect = readonly [x: number, y: number, width: number, height: number];

export interface SceneCanvas {
  width: number;
  height: number;
}

export const SCENE_CANVAS: Record<ReferenceSceneId, SceneCanvas> = {
  classroom: { width: 1586, height: 992 },
  motivation: { width: 1672, height: 941 },
  'teacher-goal': { width: 1672, height: 941 },
  'knowledge-map': { width: 1672, height: 941 },
  'personal-route': { width: 1672, height: 941 },
  'learning-cycle': { width: 1672, height: 941 },
  agency: { width: 1672, height: 941 },
  'teacher-workspace': { width: 1672, height: 941 },
  technology: { width: 1672, height: 941 },
  finale: { width: 1672, height: 941 },
};

/** Percent-based absolute positioning inside a scene canvas. */
export function rectStyle(rect: Rect, canvas: SceneCanvas): { left: string; top: string; width: string; height: string } {
  return {
    left: `${(rect[0] / canvas.width) * 100}%`,
    top: `${(rect[1] / canvas.height) * 100}%`,
    width: `${(rect[2] / canvas.width) * 100}%`,
    height: `${(rect[3] / canvas.height) * 100}%`,
  };
}

/**
 * Typography inside insets scales with the canvas, never with the viewport:
 * `font-size: calc(var(--sn-unit) * N)` where --sn-unit = 1% of canvas width
 * is set on the raster layer by the shell.
 */
export const CANVAS_UNIT_VAR = '--sn-unit';
